import { useState, useEffect, useCallback } from 'react'
import { Outlet, Navigate, useLocation } from 'react-router-dom'
import { toast } from 'sonner'
import { useAuthStore } from '../stores/authStore'
import { useNotificationStore } from '../stores/notificationStore'
import { handleOIDCCallback } from '../lib/auth'
import { useWebSocket, type WSMessage } from '../lib/websocket'
import Sidebar from '../components/sidebar/Sidebar'
import BottomNav from '../components/sidebar/BottomNav'
import Header from '../components/header/Header'
import ErrorBoundary from '../components/shared/ErrorBoundary'
import LoadingScreen from '../components/shared/LoadingScreen'
import WhatsNewModal, { hasUnseenUpdates } from '../components/shared/WhatsNewModal'
import { cn } from '../lib/utils'

export default function PortalShell() {
  const { isAuthenticated, _hasHydrated } = useAuthStore()
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [whatsNewOpen, setWhatsNewOpen] = useState(false)
  const location = useLocation()

  // Handle OIDC callback tokens in URL
  useEffect(() => {
    handleOIDCCallback()
  }, [])

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname])

  // Read sidebar preference from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('esi-sidebar-collapsed')
    if (saved === 'true') setSidebarCollapsed(true)
  }, [])

  // Auto-show What's New modal on first visit after a version bump
  useEffect(() => {
    if (hasUnseenUpdates()) setWhatsNewOpen(true)
  }, [])

  const toggleSidebar = () => {
    const next = !sidebarCollapsed
    setSidebarCollapsed(next)
    localStorage.setItem('esi-sidebar-collapsed', String(next))
  }

  // Shell-level WebSocket for notifications
  const addNotification = useNotificationStore((s) => s.addNotification)
  const preferences = useNotificationStore((s) => s.preferences)
  const syncFromServer = useNotificationStore((s) => s.syncFromServer)
  const notifSynced = useNotificationStore((s) => s.synced)

  useEffect(() => {
    if (!notifSynced) syncFromServer()
  }, [notifSynced, syncFromServer])

  const onEvent = useCallback((msg: WSMessage) => {
    const eventType = (msg.event_type || msg.type || '') as string
    const severity = (msg.severity || 'info') as 'critical' | 'warning' | 'info'
    const deviceName = (msg.device_name || msg.device_id || '') as string
    const message = (msg.message || eventType) as string
    const deviceId = (msg.device_id || '') as string

    const typeKey = eventType.replace(/-/g, '_') as keyof typeof preferences
    if (typeKey in preferences && !preferences[typeKey]) return

    if (preferences.quietHoursEnabled) {
      const now = new Date()
      const hhmm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
      const { quietHoursStart, quietHoursEnd } = preferences
      if (quietHoursStart <= quietHoursEnd
        ? hhmm >= quietHoursStart && hhmm < quietHoursEnd
        : hhmm >= quietHoursStart || hhmm < quietHoursEnd
      ) return
    }

    addNotification({
      type: eventType,
      severity,
      title: `${eventType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}`,
      message: deviceName ? `${deviceName}: ${message}` : message,
      deviceId,
      deviceName,
    })

    if (severity === 'critical') {
      toast.error(message, { description: deviceName, duration: Infinity })
    } else if (severity === 'warning') {
      toast.warning(message, { description: deviceName, duration: 8000 })
    } else {
      toast.info(message, { description: deviceName, duration: 5000 })
    }
  }, [addNotification, preferences])

  useWebSocket({ onEvent, enabled: isAuthenticated })

  if (!_hasHydrated) return <LoadingScreen />
  if (!isAuthenticated) return <Navigate to="/login" replace />

  return (
    <div className="h-screen flex overflow-hidden bg-bg-primary">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-2 focus:left-2 focus:bg-accent focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:text-sm">
        Skip to content
      </a>
      {/* Desktop sidebar */}
      <div className="hidden md:block">
        <Sidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />
      </div>

      {/* Mobile sidebar overlay */}
      {mobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/60 z-30 md:hidden"
            role="presentation"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="fixed left-0 top-0 bottom-0 z-40 md:hidden">
            <Sidebar collapsed={false} onToggle={() => setMobileMenuOpen(false)} />
          </div>
        </>
      )}

      {/* Main content */}
      <div
        className={cn(
          'flex-1 flex flex-col min-w-0 transition-all duration-200',
          sidebarCollapsed ? 'md:ml-16' : 'md:ml-60',
        )}
      >
        <Header onMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

        <main id="main-content" role="main" className="flex-1 overflow-y-auto p-4 sm:p-6 pb-20 md:pb-6">
          <ErrorBoundary resetKey={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      {/* Mobile bottom nav */}
      <BottomNav />

      <WhatsNewModal open={whatsNewOpen} onClose={() => setWhatsNewOpen(false)} />
    </div>
  )
}
